import React from 'react';
import { Typography, theme, ConfigProvider } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IconUser,
  IconShieldLock,
  IconLock,
  IconBell,
  IconReceipt2,
  IconCreditCard,
  IconLanguage,
  IconBuildingStore,
  IconArrowLeft,
  IconHeadset,
  IconCoin,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/Layouts/Layout';
import SidebarContentLayout from '../../components/Layouts/SidebarContentLayout';
import SidebarNavList from '../../components/Layouts/SidebarNavList';
import SettingsListRow from '../../components/Settings/SettingsListRow';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import PersonalInfoSection from './sections/PersonalInfoSection';
import SecuritySection from './sections/SecuritySection';
import PrivacySection from './sections/PrivacySection';
import NotificationsSection from './sections/NotificationsSection';
import TaxSection from './sections/TaxSection';
import PaymentsSection from './sections/PaymentsSection';
import LanguageSection from './sections/LanguageSection';
import CurrencySection from './sections/CurrencySection';
import HostProfileSection from './sections/HostProfileSection';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title } = Typography;

type SettingsSectionKey = 'personal' | 'security' | 'privacy' | 'notifications' | 'taxes' | 'payments' | 'language' | 'currency' | 'hostProfile';

const ALL_SECTION_KEYS: SettingsSectionKey[] = ['personal', 'security', 'privacy', 'notifications', 'taxes', 'payments', 'language', 'currency', 'hostProfile'];

const isSectionKey = (value: string | null): value is SettingsSectionKey => !!value && (ALL_SECTION_KEYS as string[]).includes(value);

const SECTIONS: { key: SettingsSectionKey; icon: React.ReactNode; labelKey: string; hostOnly?: boolean }[] = [
  { key: 'personal', icon: <IconUser size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_PERSONAL },
  { key: 'hostProfile', icon: <IconBuildingStore size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_HOST_PROFILE, hostOnly: true },
  { key: 'security', icon: <IconShieldLock size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_SECURITY },
  { key: 'privacy', icon: <IconLock size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_PRIVACY },
  { key: 'notifications', icon: <IconBell size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_NOTIFICATIONS },
  { key: 'taxes', icon: <IconReceipt2 size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_TAXES },
  { key: 'payments', icon: <IconCreditCard size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_PAYMENTS },
  { key: 'language', icon: <IconLanguage size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_LANGUAGE },
  { key: 'currency', icon: <IconCoin size={18} />, labelKey: TRANSLATIONS.SETTINGS_NAV_CURRENCY },
];

// Shared by both mobile screens below (list's "back to Perfil" and each
// detail screen's "back to the list") -- same back-arrow-plus-title header,
// just a different onBack target. Horizontal padding matches SettingsListRow
// / ProfileMobile's own rows (4px) rather than a bigger page-padding value,
// so the title/back-arrow line up with the rows below instead of sitting
// more indented than them.
const MobileSettingsHeader: React.FC<{ title: string; onBack: () => void }> = ({ title, onBack }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 4px' }}>
      <button
        type="button"
        onClick={onBack}
        aria-label={translate(t, TRANSLATIONS.BACK)}
        style={{ border: 'none', background: 'none', padding: 8, margin: '-8px 0 -8px -8px', cursor: 'pointer', display: 'flex', color: token.colorText }}
      >
        <IconArrowLeft size={22} />
      </button>
      <Title level={4} style={{ margin: 0 }}>
        {title}
      </Title>
    </div>
  );
};

const SECTION_COMPONENTS: Record<SettingsSectionKey, React.ReactNode> = {
  personal: <PersonalInfoSection />,
  hostProfile: <HostProfileSection />,
  security: <SecuritySection />,
  privacy: <PrivacySection />,
  notifications: <NotificationsSection />,
  taxes: <TaxSection />,
  payments: <PaymentsSection />,
  language: <LanguageSection />,
  currency: <CurrencySection />,
};

/**
 * Unified "Configuración" page -- sidebar of soft pill nav items (sliding
 * highlight via framer-motion's layoutId, same idiom as HomeSearchBar's
 * availability chips) next to a soft rounded content card. "Perfil de
 * anfitrión" lives here as one more section (hostOnly, hidden entirely for
 * guests) instead of its own page reached from the avatar menu -- see
 * UserProfile.tsx, which no longer links to /host/profile directly.
 */
const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const { hasHostProfile } = useHostProfile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSection = searchParams.get('section');
  const requestedSection: SettingsSectionKey = isSectionKey(rawSection) ? rawSection : 'personal';
  // A guest landing on ?section=hostProfile (stale link, direct hit) falls
  // back to Información personal rather than rendering a hidden section.
  const activeSection: SettingsSectionKey = requestedSection === 'hostProfile' && !hasHostProfile ? 'personal' : requestedSection;
  const visibleSections = SECTIONS.filter((section) => !section.hostOnly || hasHostProfile);

  // Mobile's own list->detail navigation reuses the exact same searchParams
  // mechanism as the desktop sidebar (activeSection above) instead of a
  // separate local `useState` -- deriving list-vs-detail from the URL like
  // this is what makes it immune to remounts. Saving almost any field here
  // (PersonalInfoSection/PrivacySection/NotificationsSection/LanguageSection)
  // calls refresh() on useUserProfile(), which briefly flips its `loading`
  // flag; ProtectedRoute renders a full-page spinner in place of `children`
  // for any tick where that's true, unmounting this whole page and remounting
  // it once it clears. A plain useState here was wiped on every one of those
  // remounts, dropping back to its initial value (the list) -- reading as
  // "changing a setting kicks you out to Configuración". Desktop never had
  // this bug because its own activeSection already came from the URL, so a
  // remount just re-reads the same section; this makes mobile do the same.
  const hasSectionParam = searchParams.has('section');

  if (isMobile) {
    if (!hasSectionParam) {
      return (
        <AppLayout>
          {/* width:'100%'+maxWidth:480 matches ProfileMobile's own wrapper --
              without it, this div (a flex item of AppLayout's Content, which
              is itself display:flex) shrinks to fit its content instead of
              stretching to fill the row, reading as much narrower than
              Perfil despite both living in the same Content. */}
          <div style={{ width: '100%', maxWidth: 480 }}>
            <MobileSettingsHeader title={translate(t, TRANSLATIONS.SETTINGS_PAGE_TITLE)} onBack={() => navigate('/account')} />
            <div style={{ padding: '0 4px 24px' }}>
              {visibleSections.map((section) => (
                <SettingsListRow
                  key={section.key}
                  icon={section.icon}
                  label={translate(t, section.labelKey)}
                  onClick={() => setSearchParams({ section: section.key })}
                />
              ))}
              {/* Navigates straight to /heavy-duty-machinery/support rather than an
                  internal ?section= -- this isn't one of this page's own
                  sections, it's the "contactar con soporte" entry point
                  (see that page's own doc comment on why it needs one here,
                  not just the Mensajes banner). */}
              <SettingsListRow
                icon={<IconHeadset size={18} />}
                label={translate(t, TRANSLATIONS.SETTINGS_NAV_SUPPORT)}
                onClick={() => navigate('/heavy-duty-machinery/support')}
              />
            </div>
          </div>
        </AppLayout>
      );
    }

    const activeMobileLabel = SECTIONS.find((section) => section.key === activeSection)?.labelKey;

    return (
      <AppLayout>
        {/* AppLayout's <Content> is itself `display:flex` (see Layout.tsx) and
            renders whatever is passed here as `children` directly inside that
            flex row -- every other page passes it exactly one element. This
            branch used to hand it two sibling <div>s (ConfigProvider renders
            no DOM node of its own), which the flex row laid out *side by
            side* instead of stacked: the content div got squeezed toward
            zero width (forcing its text to wrap one character per line) and
            the header row stretched to the full row height, centering the
            back arrow/title vertically in the middle of the page instead of
            pinning them to the top. A single wrapping div fixes both at
            once -- AppLayout gets one child again, header and content stack
            normally inside it. */}
        {/* Same width:'100%'+maxWidth:480 as the list screen/ProfileMobile,
            for the same reason -- otherwise this div shrinks to fit its
            content as a Content flex item instead of filling the row. */}
        <div style={{ width: '100%', maxWidth: 480 }}>
          {/* Fully-rounded Selects for the mobile detail view only -- the
              accent itself comes from the app-wide theme now. */}
          <ConfigProvider theme={{ components: { Select: { borderRadius: 999 } } }}>
            <MobileSettingsHeader
              title={activeMobileLabel ? translate(t, activeMobileLabel) : ''}
              onBack={() => setSearchParams({})}
            />
            <div style={{ padding: '4px 4px 24px' }}>{SECTION_COMPONENTS[activeSection]}</div>
          </ConfigProvider>
        </div>
      </AppLayout>
    );
  }

  const cardShellStyle: React.CSSProperties = {
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 20,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 8px 20px rgba(0,0,0,0.04)',
  };

  const sidebarNav = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <SidebarNavList
        activeKey={activeSection}
        onSelect={(key) => setSearchParams({ section: key })}
        pillLayoutId="settings-nav-pill"
        items={visibleSections.map((section) => ({
          key: section.key,
          icon: section.icon,
          label: translate(t, section.labelKey),
        }))}
      />

      {/* Same "contactar con soporte" entry point as the mobile list above --
          navigates straight to /heavy-duty-machinery/support, not an internal ?section=,
          so it's visually set apart (its own top border) rather than folded
          into the sliding-pill selection state the real sections share. */}
      <button
        type="button"
        onClick={() => navigate('/heavy-duty-machinery/support')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 8,
          padding: '12px 12px 10px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          borderRadius: 0,
          textAlign: 'left',
          cursor: 'pointer',
          background: 'transparent',
          color: token.colorTextSecondary,
          fontSize: 15,
        }}
      >
        <IconHeadset size={18} />
        {translate(t, TRANSLATIONS.SETTINGS_NAV_SUPPORT)}
      </button>
    </div>
  );

  return (
    <AppLayout>
      <SidebarContentLayout
        title={
          <Title level={4} style={{ marginBottom: 20, paddingLeft: 12 }}>
            {translate(t, TRANSLATIONS.SETTINGS_PAGE_TITLE)}
          </Title>
        }
        sidebar={sidebarNav}
        activeKey={activeSection}
        wrapperStyle={{ width: '100%', maxWidth: 1100 }}
        sidebarStyle={{ padding: '24px 16px', ...cardShellStyle }}
        contentStyle={{ padding: '28px 32px', overflow: 'hidden', ...cardShellStyle }}
      >
        {/* Pills every Select in Settings (Idioma/zona horaria, canales de
            notificación...) to match the rest of the redesign -- scoped
            here rather than app-wide so HeavyDutyMachineryForm's many Selects
            etc. are untouched. */}
        <ConfigProvider theme={{ components: { Select: { borderRadius: 999 } } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {SECTION_COMPONENTS[activeSection]}
            </motion.div>
          </AnimatePresence>
        </ConfigProvider>
      </SidebarContentLayout>
    </AppLayout>
  );
};

export default Settings;
